import styled from '@emotion/styled';
import {
  ButtonNormalPrimaryMediumRightIconCompact,
  TextParagraph,
  token,
} from '@ferreri/prever-component-lib';
import { LinkWrapper } from '@ferreri/prever-component-lib/nextjs';
import { useRouter } from 'next/router';

import { BenefitCategories } from './BenefitCategories';
import { Benefit } from './benefits';

type BenefitsCardProps = {
  benefit: Benefit;
};

export const BenefitsCard = ({ benefit }: BenefitsCardProps) => {
  const router = useRouter();
  const imageSrc = `${router.basePath}/assets/img/${benefit.image}`;

  return (
    <Card>
      <CardImage src={imageSrc} alt={benefit.title} />
      <CardOverlay>
        <CardText>
          <CardTitle>{benefit.title}</CardTitle>
          <CardDescription>{benefit.description}</CardDescription>
          <BenefitCategories categories={benefit.categories} />
        </CardText>

        <LinkWrapper href={benefit.buttonUrl}>
          <ButtonNormalPrimaryMediumRightIconCompact
            icon={
              <ArrowIcon
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </ArrowIcon>
            }
          >
            {benefit.buttonText}
          </ButtonNormalPrimaryMediumRightIconCompact>
        </LinkWrapper>
      </CardOverlay>
    </Card>
  );
};

const Card = styled.div`
  position: relative;
  flex-shrink: 0;
  width: calc(100vw / 1.35);
  height: auto;
  aspect-ratio: 3/5;
  overflow: hidden;
  border-radius: 12px;

  @media (min-width: 768px) {
    width: calc(100vw / 1.55);
    height: auto;
    aspect-ratio: 4/2;
    border-radius: 16px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${token.spacing.spacingLarge};
  display: flex;
  flex-direction: column;
  gap: ${token.spacing.spacingMedium};
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.6) 50%,
    transparent 100%
  );

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${token.spacing.spacingSmall};
  }
`;

const CardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${token.spacing.spacingXSmall};
  flex: 1;

  @media (min-width: 768px) {
    max-width: 350px;
  }
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  font-family: ${token.typography.fontFamily01};
  margin: 0;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const CardDescription = styled(TextParagraph)`
  color: #ffffff !important;
  font-size: 12px;
  margin: 0;
  line-height: 1.4;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const ArrowIcon = styled.svg`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-left: 8px;
  vertical-align: middle;
`;
